import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Plus, Search, Trash2 } from "lucide-react";
import { fetchQuestions } from "../../redux/slices/questionSlice";
import { pickLocalized } from "../../lib/localized";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import {
  questionListClass,
  questionListClassTall,
  questionRowHover,
} from "../../lib/quizStyles";

/**
 * Select and order quiz questions for a course day, daily quiz, etc.
 * Each item: { question_id, order_number, marks_override?, question_text?, question_type? }
 */
function QuestionPicker({ value = [], onChange, disabled = false }) {
  const dispatch = useDispatch();
  const { questions: availableQuestions = [] } = useSelector(
    (s) => s.question
  );
  const [search, setSearch] = useState("");

  useEffect(() => {
    dispatch(fetchQuestions({ limit: 200, page: 1 }));
  }, [dispatch]);

  const selectedIds = new Set(value.map((q) => q.question_id));

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return availableQuestions;
    return availableQuestions.filter((question) => {
      const text = pickLocalized(question.question_text).toLowerCase();
      return text.includes(q) || question.id?.toLowerCase().includes(q);
    });
  }, [availableQuestions, search]);

  const add = (question) => {
    if (disabled || selectedIds.has(question.id)) return;
    onChange([
      ...value,
      {
        question_id: question.id,
        order_number: value.length + 1,
        marks_override: question.default_marks || null,
        question_text: question.question_text,
        question_type: question.question_type,
        default_marks: question.default_marks,
      },
    ]);
  };

  const remove = (questionId) => {
    if (disabled) return;
    const next = value
      .filter((q) => q.question_id !== questionId)
      .map((q, i) => ({ ...q, order_number: i + 1 }));
    onChange(next);
  };

  const updateMarks = (questionId, marks) => {
    onChange(
      value.map((q) =>
        q.question_id === questionId
          ? {
              ...q,
              marks_override: marks === "" ? null : parseInt(marks, 10),
            }
          : q
      )
    );
  };

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label>Quiz questions ({value.length})</Label>
        <p className="text-xs text-muted-foreground">
          Add questions learners answer for this day. Order matches the quiz
          sequence in the app.
        </p>
        <div className={questionListClass}>
          {value.length === 0 ? (
            <p className="p-4 text-center text-sm text-muted-foreground">
              No questions yet — search below to add some.
            </p>
          ) : (
            <div className="divide-y divide-border">
              {value.map((q, index) => (
                <div
                  key={q.question_id}
                  className="p-3 flex flex-wrap items-center gap-2 sm:gap-3"
                >
                  <span className="text-sm text-muted-foreground w-6">
                    {index + 1}.
                  </span>
                  <span className="flex-1 text-sm min-w-[120px]">
                    {pickLocalized(q.question_text) || "Question"}
                  </span>
                  <div className="flex items-center gap-2">
                    <Label className="text-xs text-muted-foreground whitespace-nowrap">
                      Points
                    </Label>
                    <Input
                      type="number"
                      min={1}
                      className="w-16 h-8 text-xs"
                      disabled={disabled}
                      value={q.marks_override ?? q.default_marks ?? ""}
                      onChange={(e) =>
                        updateMarks(q.question_id, e.target.value)
                      }
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      disabled={disabled}
                      onClick={() => remove(q.question_id)}
                    >
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="space-y-2">
        <Label>Add from question bank</Label>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search questions…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10"
            disabled={disabled}
          />
        </div>
        <div className={questionListClassTall}>
          {filtered.length === 0 ? (
            <p className="p-4 text-center text-sm text-muted-foreground">
              No questions match. Create questions under Quiz → Questions
              first.
            </p>
          ) : (
            <div className="divide-y divide-border">
              {filtered.map((question) => (
                <div
                  key={question.id}
                  className={`p-3 flex items-center gap-3 ${questionRowHover}`}
                >
                  <span className="flex-1 text-sm">
                    {pickLocalized(question.question_text) || "Untitled"}
                  </span>
                  <Badge variant="secondary" className="text-xs">
                    {question.question_type}
                  </Badge>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    disabled={disabled || selectedIds.has(question.id)}
                    onClick={() => add(question)}
                  >
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default QuestionPicker;
